import React from 'react';

interface LevelSurroundingArtworkProps {
  levelId: number;
  baseColor: string;
  isRestored?: boolean;
  isNight?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const LevelSurroundingArtwork: React.FC<LevelSurroundingArtworkProps> = React.memo(({
  levelId,
  baseColor,
  isRestored = false,
  isNight = false,
  className = "",
  style = {}
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState<number>(() => {
    return typeof window !== 'undefined' ? window.innerWidth : 480;
  });

  React.useEffect(() => {
    let timer: number | null = null;
    let lastW = 0;

    const scheduleUpdate = (newW: number) => {
      const rounded = Math.round(newW);
      if (rounded <= 0 || Math.abs(rounded - lastW) < 4) return;
      if (timer) clearTimeout(timer);
      timer = window.setTimeout(() => {
        lastW = rounded;
        setContainerWidth((prev) => (prev === rounded ? prev : rounded));
      }, 80);
    };

    const updateSize = () => {
      if (containerRef.current) {
        const w = containerRef.current.clientWidth || window.innerWidth;
        scheduleUpdate(w);
      }
    };

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect?.width || entry.contentBoxSize?.[0]?.inlineSize;
        if (w && w > 0) {
          scheduleUpdate(w);
        }
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    window.addEventListener('resize', updateSize, { passive: true });

    return () => {
      if (timer) clearTimeout(timer);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  // Center of the game board is at coordinate x = 200.
  // To ensure 1 SVG unit = 1 CSS pixel without distortion or stretching:
  // viewBoxWidth = containerWidth, viewBoxLeft = 200 - containerWidth / 2
  const w = Math.max(400, containerWidth);
  const viewBoxLeft = Math.round(200 - w / 2);
  const viewBoxWidth = w;
  const viewBoxTop = -2000;
  const viewBoxHeight = 2000;

  // Generate fence posts every 55px across the visible screen width
  const postSpacing = 55;
  const startPost = Math.floor((viewBoxLeft - 60) / postSpacing) * postSpacing + 35;
  const endPost = Math.ceil((viewBoxLeft + viewBoxWidth + 60) / postSpacing) * postSpacing;
  const posts = React.useMemo(() => {
    const list: number[] = [];
    for (let x = startPost; x <= endPost; x += postSpacing) {
      list.push(x);
    }
    return list;
  }, [startPost, endPost]);

  return (
    <div
      ref={containerRef}
      className={`absolute left-0 w-full pointer-events-none z-0 overflow-visible select-none ${className}`}
      style={style}
    >
      <svg
        className="w-full overflow-visible"
        style={{
          position: 'absolute',
          top: `${viewBoxTop}px`,
          left: 0,
          width: '100%',
          height: `${viewBoxHeight}px`,
        }}
        viewBox={`${viewBoxLeft} ${viewBoxTop} ${viewBoxWidth} ${viewBoxHeight}`}
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* ========================================================= */}
          {/* 1. GRADIENTS POUR LES HORIZONS (Ciel au-dessus de la stèle) */}
          {/* ========================================================= */}
          
          {/* Horizon Level 0: Taddart (Ciel azur méditerranéen & Aube dorée) */}
          <linearGradient id="sky_taddart" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0b2b5c" />
            <stop offset="20%" stopColor="#0f3b7a" />
            <stop offset="45%" stopColor="#0284c7" />
            <stop offset="70%" stopColor="#38bdf8" />
            <stop offset="85%" stopColor="#7dd3fc" />
            <stop offset="94%" stopColor="#bae6fd" />
            <stop offset="100%" stopColor="#fef08a" />
          </linearGradient>

          {/* Horizon Level 1: Azemmur (Coucher de soleil ambré & chaud) */}
          <linearGradient id="sky_azemmur" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1e1b4b" />
            <stop offset="25%" stopColor="#3730a3" />
            <stop offset="50%" stopColor="#b45309" />
            <stop offset="75%" stopColor="#f59e0b" />
            <stop offset="92%" stopColor="#fbbf24" />
            <stop offset="100%" stopColor="#fef08a" />
          </linearGradient>

          {/* Horizon Level 2: Djurdjura (Grand Massif Alpin sous la Nuit Étoilée) */}
          <linearGradient id="sky_djurdjura" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#01040f" />
            <stop offset="25%" stopColor="#020617" />
            <stop offset="50%" stopColor="#08142c" />
            <stop offset="75%" stopColor="#0f1f3e" />
            <stop offset="88%" stopColor="#172e54" />
            <stop offset="96%" stopColor="#1e293b" />
            <stop offset="100%" stopColor="#0f172a" />
          </linearGradient>

          {/* Horizon Level 3: Iɣil (Crépuscule Mystique Indigo & Pourpre Céleste) */}
          <linearGradient id="sky_igil" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#070318" />
            <stop offset="25%" stopColor="#0c0728" />
            <stop offset="50%" stopColor="#1e1b4b" />
            <stop offset="70%" stopColor="#4c1d95" />
            <stop offset="85%" stopColor="#7e22ce" />
            <stop offset="95%" stopColor="#c026d3" />
            <stop offset="100%" stopColor="#f472b6" />
          </linearGradient>

          {/* Horizon Level 4 Corrompu: Ijduden (Orage Toxique & Brume Noire) */}
          <linearGradient id="sky_ijduden_dark" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#010d0a" />
            <stop offset="30%" stopColor="#021a14" />
            <stop offset="60%" stopColor="#064e3b" />
            <stop offset="80%" stopColor="#065f46" />
            <stop offset="92%" stopColor="#0f172a" />
            <stop offset="100%" stopColor="#1e293b" />
          </linearGradient>

          {/* Horizon Level 4 Nuit Épilogue: Lǧemɛa n Tizi sous la lune Ayyur */}
          <linearGradient id="sky_ijduden_night" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#020617" />
            <stop offset="25%" stopColor="#090d1f" />
            <stop offset="50%" stopColor="#064e3b" />
            <stop offset="75%" stopColor="#022c22" />
            <stop offset="90%" stopColor="#0f172a" />
            <stop offset="100%" stopColor="#064e3b" />
          </linearGradient>

          {/* Horizon Level 4 Restauré: Lǧemɛa n Tizi (Émeraude & Ciel Azur Céleste) */}
          <linearGradient id="sky_ijduden_restored" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0369a1" />
            <stop offset="25%" stopColor="#0284c7" />
            <stop offset="50%" stopColor="#38bdf8" />
            <stop offset="70%" stopColor="#10b981" />
            <stop offset="88%" stopColor="#6ee7b7" />
            <stop offset="100%" stopColor="#fef08a" />
          </linearGradient>

          {/* Filters */}
          <filter id="sunGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
          <filter id="celestialGlow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>

          {/* Reusable Tiny Kabyle Ridge House */}
          <g id="ridge_house_small">
            <rect x="-6" y="-5" width="12" height="6" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="0.5" />
            <polygon points="-8,-5 0,-10 8,-5" fill="#dc2626" />
            <rect x="-1.5" y="-2" width="3" height="3" fill="#78350f" />
          </g>

          {/* Reusable Distant Mountain Olive Tree */}
          <g id="distant_ridge_tree">
            <line x1="0" y1="0" x2="0" y2="-4" stroke="#451a03" strokeWidth="1" />
            <ellipse cx="0" cy="-6" rx="4" ry="3" fill="#15803d" />
          </g>
        </defs>

        {/* ========================================================================= */}
        {/* 1. HORIZON DU HAUT (-800px à 0px) : PAYSAGES KABYLES PANORAMIQUES PLEINE LARGEUR */}
        {/* ========================================================================= */}

        {/* --- LEVEL 0 : TADDART (Village Ancestral & Contreforts du Djurdjura) --- */}
        {levelId === 0 && (
          <g id="horizon_level_0">
            {/* Ciel azur panoramique pleine largeur */}
            <rect x="-3000" y="-2000" width="6400" height="2000" fill="url(#sky_taddart)" />
            
            {/* Grand Soleil Doré Matinal & Rayonnement au centre */}
            <g filter="url(#sunGlow)">
              <circle cx="200" cy="-280" r="36" fill="#fef08a" opacity="0.95" />
              <circle cx="200" cy="-280" r="60" fill="#fde047" opacity="0.4" />
              <circle cx="200" cy="-280" r="90" fill="#fef08a" opacity="0.15" />
            </g>

            {/* Nuages blancs cotonneux d'altitude sur toute l'étendue du ciel */}
            {/* Nuages centre */}
            <ellipse cx="70" cy="-360" rx="55" ry="10" fill="#ffffff" opacity="0.7" />
            <ellipse cx="60" cy="-364" rx="35" ry="8" fill="#ffffff" opacity="0.85" />
            <ellipse cx="330" cy="-330" rx="65" ry="12" fill="#ffffff" opacity="0.65" />
            <ellipse cx="345" cy="-334" rx="40" ry="9" fill="#ffffff" opacity="0.8" />
            <ellipse cx="160" cy="-380" rx="45" ry="8" fill="#ffffff" opacity="0.5" />
            {/* Nuages gauche */}
            <ellipse cx="-250" cy="-340" rx="60" ry="11" fill="#ffffff" opacity="0.65" />
            <ellipse cx="-550" cy="-370" rx="75" ry="12" fill="#ffffff" opacity="0.7" />
            <ellipse cx="-900" cy="-330" rx="65" ry="10" fill="#ffffff" opacity="0.6" />
            <ellipse cx="-1350" cy="-360" rx="80" ry="13" fill="#ffffff" opacity="0.65" />
            <ellipse cx="-1850" cy="-340" rx="70" ry="11" fill="#ffffff" opacity="0.6" />
            {/* Nuages droite */}
            <ellipse cx="650" cy="-350" rx="65" ry="11" fill="#ffffff" opacity="0.65" />
            <ellipse cx="1000" cy="-370" rx="75" ry="12" fill="#ffffff" opacity="0.7" />
            <ellipse cx="1450" cy="-340" rx="80" ry="13" fill="#ffffff" opacity="0.65" />
            <ellipse cx="1950" cy="-360" rx="70" ry="11" fill="#ffffff" opacity="0.6" />

            {/* Aigles / Hirondelles en vol dans le grand ciel */}
            <path d="M 120 -340 Q 124 -345 128 -340 Q 132 -345 136 -340" fill="none" stroke="#0f172a" strokeWidth="1.4" opacity="0.8" />
            <path d="M 145 -355 Q 148 -359 152 -355 Q 155 -359 159 -355" fill="none" stroke="#0f172a" strokeWidth="1.1" opacity="0.7" />
            <path d="M 275 -315 Q 280 -320 285 -315 Q 290 -320 295 -315" fill="none" stroke="#0f172a" strokeWidth="1.3" opacity="0.8" />
            <path d="M -320 -330 Q -315 -335 -310 -330 Q -305 -335 -300 -330" fill="none" stroke="#0f172a" strokeWidth="1.3" opacity="0.75" />
            <path d="M 720 -325 Q 725 -330 730 -325 Q 735 -330 740 -325" fill="none" stroke="#0f172a" strokeWidth="1.3" opacity="0.75" />

            {/* 1ère Chaîne : Hautes Cimes Lointaines du Djurdjura (Panoramique étendu de -3000 à 3400) */}
            <polygon
              points="
                -3000,-120 -2800,-210 -2550,-150 -2300,-220 -2050,-160 -1800,-230 -1550,-150 -1300,-210 -1050,-160 -850,-220 -600,-150 -400,-210 -200,-160
                -50,-150 -10,-190 50,-140 120,-220 180,-170 250,-230 330,-150 400,-210 450,-130
                650,-220 850,-150 1100,-230 1350,-160 1600,-220 1850,-150 2100,-210 2350,-160 2600,-230 2850,-150 3100,-210 3400,-120
                3400,0 -3000,0
              "
              fill="#0284c7"
              opacity="0.35"
            />

            {/* 2ème Chaîne : Massifs Intermédiaires (Vert forêt) */}
            <polygon
              points="
                -3000,-40 -2700,-110 -2400,-70 -2100,-120 -1800,-80 -1500,-130 -1200,-75 -900,-120 -600,-80 -300,-110
                -50,-75 140,-130 200,-90 450,-80
                700,-120 1000,-75 1300,-130 1600,-80 1900,-120 2200,-70 2500,-110 2800,-80 3100,-120 3400,-40
                3400,0 -3000,0
              "
              fill="#166534"
              opacity="0.4"
            />

            {/* 3ème Chaîne : Crêtes Boisées & Collines Verdoyantes */}
            <path
              d="
                M -3000 -60
                Q -2800 -105 -2600 -70 Q -2400 -45 -2200 -80 Q -2000 -110 -1800 -70
                Q -1600 -45 -1400 -85 Q -1200 -115 -1000 -70 Q -800 -45 -600 -80
                Q -400 -105 -200 -65 Q 0 -40 200 -85 Q 400 -110 600 -70
                Q 800 -45 1000 -80 Q 1200 -115 1400 -70 Q 1600 -45 1800 -80
                Q 2000 -110 2200 -70 Q 2400 -45 2600 -85 Q 2800 -115 3000 -70
                Q 3200 -45 3400 -60
                L 3400 0 L -3000 0 Z
              "
              fill="#166534"
            />
            <path
              d="
                M -3000 -40
                Q -2800 -75 -2600 -45 Q -2400 -30 -2200 -60 Q -2000 -80 -1800 -50
                Q -1600 -30 -1400 -60 Q -1200 -85 -1000 -50 Q -800 -30 -600 -60
                Q -400 -75 -200 -45 Q 0 -25 200 -60 Q 400 -80 600 -45
                Q 800 -30 1000 -60 Q 1200 -85 1400 -50 Q 1600 -30 1800 -60
                Q 2000 -75 2200 -45 Q 2400 -30 2600 -60 Q 2800 -85 3000 -50
                Q 3200 -30 3400 -40
                L 3400 0 L -3000 0 Z
              "
              fill="#15803d"
            />

            {/* Village Kabyle perché sur la crête au centre (Maisons en pierre & toits de tuiles rouges groupées) */}
            <use href="#ridge_house_small" x="140" y="-82" transform="scale(0.85)" />
            <use href="#ridge_house_small" x="156" y="-80" transform="scale(0.75)" />
            <use href="#ridge_house_small" x="170" y="-78" transform="scale(0.9)" />
            <rect x="180" y="-90" width="3" height="12" fill="#e2e8f0" />
            <polygon points="179,-90 181.5,-95 184,-90" fill="#dc2626" />

            {/* Petits hameaux kabyles lointains sur les crêtes latérales */}
            <use href="#ridge_house_small" x="-520" y="-76" transform="scale(0.75)" />
            <use href="#ridge_house_small" x="-508" y="-74" transform="scale(0.7)" />
            <use href="#ridge_house_small" x="840" y="-76" transform="scale(0.75)" />
            <use href="#ridge_house_small" x="852" y="-74" transform="scale(0.7)" />

            {/* Oliviers et végétation d'altitude répartis sur la crête */}
            <use href="#distant_ridge_tree" x="80" y="-65" />
            <use href="#distant_ridge_tree" x="95" y="-68" />
            <use href="#distant_ridge_tree" x="250" y="-56" />
            <use href="#distant_ridge_tree" x="265" y="-58" />
            <use href="#distant_ridge_tree" x="380" y="-48" />
            <use href="#distant_ridge_tree" x="-320" y="-55" />
            <use href="#distant_ridge_tree" x="-340" y="-58" />
            <use href="#distant_ridge_tree" x="-750" y="-50" />
            <use href="#distant_ridge_tree" x="620" y="-55" />
            <use href="#distant_ridge_tree" x="640" y="-58" />
            <use href="#distant_ridge_tree" x="1100" y="-52" />

            {/* Raccord doux et harmonieux vers le sommet du plateau */}
            <path
              d="
                M -3000 -25
                Q -2800 -50 -2600 -30 Q -2400 -18 -2200 -40 Q -2000 -55 -1800 -30
                Q -1600 -18 -1400 -40 Q -1200 -55 -1000 -30 Q -800 -18 -600 -40
                Q -400 -50 -200 -25 Q 0 -15 200 -40 Q 400 -55 600 -30
                Q 800 -18 1000 -40 Q 1200 -55 1400 -30 Q 1600 -18 1800 -40
                Q 2000 -50 2200 -25 Q 2400 -18 2600 -40 Q 2800 -55 3000 -30
                Q 3200 -18 3400 -25
                L 3400 0 L -3000 0 Z
              "
              fill="#15803d"
            />
          </g>
        )}

        {/* --- LEVEL 1 : AZEMMUR (Oliveraies Dorées & Coucher de Soleil) --- */}
        {levelId === 1 && (
          <g id="horizon_level_1">
            {/* Ciel crépusculaire chaud pleine largeur */}
            <rect x="-3000" y="-2000" width="6400" height="2000" fill="url(#sky_azemmur)" />

            {/* Soleil couchant rond et bas sur l'horizon derrière les montagnes */}
            <g id="sunset_sun_azemmur">
              {/* Halos dorés chauds du coucher de soleil */}
              <circle cx="200" cy="-130" r="65" fill="#f59e0b" opacity="0.28" filter="url(#sunGlow)" />
              <circle cx="200" cy="-130" r="45" fill="#fbbf24" opacity="0.38" filter="url(#sunGlow)" />
              {/* Disque solaire rond d'or couchant */}
              <circle cx="200" cy="-130" r="30" fill="#f59e0b" opacity="0.9" />
              <circle cx="200" cy="-130" r="28" fill="#fbbf24" />
              <circle cx="200" cy="-130" r="24" fill="#fffbeb" opacity="0.95" />
            </g>

            {/* Falaises et montagnes lointaines du contrefort atlasique (calque le plus en profondeur abaissé pour laisser apparaître le soleil) */}
            <polygon
              points="
                -3000,-60 -2700,-135 -2400,-80 -2100,-140 -1800,-90 -1500,-150 -1200,-95 -900,-140 -600,-90 -300,-135
                -50,-95 -10,-125 70,-85 160,-135 200,-105 240,-100 340,-145 450,-85
                700,-135 950,-90 1250,-140 1550,-95 1850,-145 2150,-90 2450,-140 2750,-85 3050,-135 3400,-60
                3400,0 -3000,0
              "
              fill="#9a4c10"
            />

            {/* 1ère Colline d'oliveraies (Vert olive profond en arrière-plan) */}
            <path
              d="
                M -3000 -45
                Q -2800 -85 -2600 -55 Q -2400 -35 -2200 -65 Q -2000 -90 -1800 -55
                Q -1600 -35 -1400 -70 Q -1200 -95 -1000 -55 Q -800 -35 -600 -65
                Q -400 -85 -200 -50 Q 0 -30 200 -68 Q 400 -90 600 -55
                Q 800 -35 1000 -65 Q 1200 -95 1400 -55 Q 1600 -35 1800 -65
                Q 2000 -90 2200 -55 Q 2400 -35 2600 -70 Q 2800 -95 3000 -55
                Q 3200 -35 3400 -45
                L 3400 0 L -3000 0 Z
              "
              fill="#365314"
              opacity="0.85"
            />

            {/* 2ème Colline médiane (Vert olive doré et chaleureux) */}
            <path
              d="
                M -3000 -30
                Q -2800 -60 -2600 -35 Q -2400 -20 -2200 -45 Q -2000 -65 -1800 -38
                Q -1600 -22 -1400 -48 Q -1200 -68 -1000 -38 Q -800 -22 -600 -45
                Q -400 -60 -200 -35 Q 0 -18 200 -45 Q 400 -65 600 -35
                Q 800 -22 1000 -45 Q 1200 -68 1400 -38 Q 1600 -22 1800 -45
                Q 2000 -60 2200 -35 Q 2400 -22 2600 -48 Q 2800 -68 3000 -38
                Q 3200 -22 3400 -30
                L 3400 0 L -3000 0 Z
              "
              fill="#3f6212"
            />

            {/* 3ème Colline rapprochée (Vert tendre et ensoleillé) */}
            <path
              d="
                M -3000 -25
                Q -2800 -50 -2600 -30 Q -2400 -18 -2200 -40 Q -2000 -55 -1800 -30
                Q -1600 -18 -1400 -40 Q -1200 -55 -1000 -30 Q -800 -18 -600 -40
                Q -400 -50 -200 -25 Q 0 -15 200 -40 Q 400 -55 600 -30
                Q 800 -18 1000 -40 Q 1200 -55 1400 -30 Q 1600 -18 1800 -40
                Q 2000 -50 2200 -25 Q 2400 -18 2600 -40 Q 2800 -55 3000 -30
                Q 3200 -18 3400 -25
                L 3400 0 L -3000 0 Z
              "
              fill="#4d7c0f"
            />

            {/* Raccord doux et harmonieux avec la bordure du plateau */}
            <path
              d="
                M -3000 -12
                Q -2800 -25 -2600 -15 Q -2400 -8 -2200 -20 Q -2000 -25 -1800 -15
                Q -1600 -8 -1400 -20 Q -1200 -25 -1000 -15 Q -800 -8 -600 -20
                Q -400 -25 -200 -12 Q 0 -6 200 -18 Q 400 -25 600 -12
                Q 800 -8 1000 -18 Q 1200 -25 1400 -12 Q 1600 -8 1800 -18
                Q 2000 -25 2200 -12 Q 2400 -8 2600 -18 Q 2800 -25 3000 -12
                Q 3200 -8 3400 -12
                L 3400 0 L -3000 0 Z
              "
              fill="#65a30d"
            />
          </g>
        )}

        {/* --- LEVEL 2 : DJURDJURA (Grand Massif Alpin sous la Nuit Étoilée & Clair de Lune) --- */}
        {levelId === 2 && (
          <g id="horizon_level_2">
            {/* Ciel nocturne profond d'altitude pleine largeur */}
            <rect x="-3000" y="-2000" width="6400" height="2000" fill="url(#sky_djurdjura)" />

            {/* Halo et Lune Argentée de haute montagne */}
            <ellipse cx="320" cy="-330" rx="38" ry="38" fill="#38bdf8" opacity="0.08" filter="url(#celestialGlow)" />
            <circle cx="320" cy="-330" rx="14" ry="14" fill="#f8fafc" opacity="0.95" />
            <circle cx="324" cy="-334" rx="12" ry="12" fill="#08142c" />
            <circle cx="317" cy="-327" r="1.5" fill="#cbd5e1" opacity="0.7" />

            {/* Constellations et étoiles scintillantes réparties sur tout le ciel panoramique */}
            {/* Étoiles centrales */}
            <circle cx="40" cy="-400" r="1.6" fill="#ffffff" opacity="0.95" />
            <circle cx="85" cy="-360" r="1.2" fill="#bae6fd" opacity="0.8" />
            <circle cx="130" cy="-410" r="1.8" fill="#ffffff" opacity="0.9" />
            <circle cx="165" cy="-345" r="1.1" fill="#e2e8f0" opacity="0.75" />
            <circle cx="195" cy="-395" r="1.7" fill="#ffffff" opacity="0.95" />
            <circle cx="230" cy="-365" r="1.3" fill="#bae6fd" opacity="0.85" />
            <circle cx="270" cy="-415" r="1.5" fill="#ffffff" opacity="0.9" />
            <circle cx="380" cy="-390" r="1.7" fill="#ffffff" opacity="0.95" />
            <circle cx="410" cy="-340" r="1.2" fill="#bae6fd" opacity="0.8" />
            <circle cx="105" cy="-300" r="1.4" fill="#ffffff" opacity="0.85" />
            <circle cx="250" cy="-290" r="1.3" fill="#ffffff" opacity="0.8" />
            <circle cx="370" cy="-280" r="1.2" fill="#e2e8f0" opacity="0.75" />
            {/* Étoiles flanc gauche */}
            <circle cx="-150" cy="-380" r="1.6" fill="#ffffff" opacity="0.9" />
            <circle cx="-350" cy="-410" r="1.4" fill="#bae6fd" opacity="0.85" />
            <circle cx="-550" cy="-360" r="1.7" fill="#ffffff" opacity="0.95" />
            <circle cx="-750" cy="-390" r="1.3" fill="#e2e8f0" opacity="0.8" />
            <circle cx="-1050" cy="-415" r="1.6" fill="#ffffff" opacity="0.9" />
            <circle cx="-1400" cy="-370" r="1.5" fill="#bae6fd" opacity="0.85" />
            <circle cx="-1800" cy="-400" r="1.7" fill="#ffffff" opacity="0.95" />
            {/* Étoiles flanc droit */}
            <circle cx="580" cy="-385" r="1.6" fill="#ffffff" opacity="0.9" />
            <circle cx="820" cy="-410" r="1.4" fill="#bae6fd" opacity="0.85" />
            <circle cx="1100" cy="-365" r="1.7" fill="#ffffff" opacity="0.95" />
            <circle cx="1450" cy="-395" r="1.3" fill="#e2e8f0" opacity="0.8" />
            <circle cx="1850" cy="-410" r="1.6" fill="#ffffff" opacity="0.9" />

            {/* Étoile polaire à éclat en croix */}
            <g transform="translate(195, -395)">
              <line x1="-5" y1="0" x2="5" y2="0" stroke="#ffffff" strokeWidth="0.7" opacity="0.9" />
              <line x1="0" y1="-5" x2="0" y2="5" stroke="#ffffff" strokeWidth="0.7" opacity="0.9" />
            </g>
            <g transform="translate(-550, -360)">
              <line x1="-4" y1="0" x2="4" y2="0" stroke="#ffffff" strokeWidth="0.6" opacity="0.85" />
              <line x1="0" y1="-4" x2="0" y2="4" stroke="#ffffff" strokeWidth="0.6" opacity="0.85" />
            </g>
            <g transform="translate(1100, -365)">
              <line x1="-4" y1="0" x2="4" y2="0" stroke="#ffffff" strokeWidth="0.6" opacity="0.85" />
              <line x1="0" y1="-4" x2="0" y2="4" stroke="#ffffff" strokeWidth="0.6" opacity="0.85" />
            </g>

            {/* Volutes d'aurore nocturne subtile */}
            <ellipse cx="200" cy="-310" rx="600" ry="24" fill="#38bdf8" opacity="0.08" />

            {/* Silhouette de rapace nocturne planant sous les étoiles */}
            <path d="M 115 -320 Q 122 -326 129 -320 Q 136 -326 143 -320" fill="none" stroke="#020617" strokeWidth="1.8" strokeLinecap="round" opacity="0.9" />

            {/* ========================================================================= */}
            {/* 1. HAUTE CHAÎNE DU DJURDJURA SOUS LA NUIT (SOMMETS OBSIDIENNE & NEIGE CLAIRE) */}
            {/* ========================================================================= */}

            {/* Sommets latéraux Ouest étendant la chaîne alpine vers la gauche */}
            {[-1200, -900, -600, -350, -150].map((px, i) => (
              <g key={`djurdjura_west_peak_${i}`}>
                <polygon points={`${px - 100},0 ${px},-155 ${px},0`} fill="#1e293b" />
                <polygon points={`${px},-155 ${px + 90},0 ${px},0`} fill="#090d1f" />
                <polygon points={`${px},-155 ${px - 30},-110 ${px},-110`} fill="#f8fafc" />
                <polygon points={`${px},-155 ${px},-110 ${px + 28},-110`} fill="#94a3b8" />
              </g>
            ))}

            {/* --- SOMMET 1 (Centre Gauche) : THALETAT / MAIN DU JUIF (Sommet à 60, -150) --- */}
            <polygon points="-30,0 60,-150 60,0" fill="#1e293b" />
            <polygon points="60,-150 140,0 60,0" fill="#090d1f" />
            <polygon points="60,-150 33,-105 60,-105" fill="#f8fafc" />
            <polygon points="60,-150 60,-105 84,-105" fill="#94a3b8" />

            {/* --- SOMMET 2 (Centre) : LALLA KHEDIDJA (Tamgut n Ǧerǧer - Sommet à 200, -180) --- */}
            <polygon points="70,0 200,-180 200,0" fill="#1e293b" />
            <polygon points="200,-180 330,0 200,0" fill="#020617" />
            <polygon points="200,-180 157,-120 200,-120" fill="#f8fafc" />
            <polygon points="200,-180 200,-120 243,-120" fill="#94a3b8" />

            {/* --- SOMMET 3 (Centre Droite) : RAS TIMEDOUINE / TAMGOUT (Sommet à 335, -150) --- */}
            <polygon points="255,0 335,-150 335,0" fill="#1e293b" />
            <polygon points="335,-150 425,0 335,0" fill="#090d1f" />
            <polygon points="335,-150 311,-105 335,-105" fill="#f8fafc" />
            <polygon points="335,-150 335,-105 362,-105" fill="#94a3b8" />

            {/* Sommets latéraux Est étendant la chaîne alpine vers la droite */}
            {[550, 800, 1100, 1400, 1700].map((px, i) => (
              <g key={`djurdjura_east_peak_${i}`}>
                <polygon points={`${px - 90},0 ${px},-155 ${px},0`} fill="#1e293b" />
                <polygon points={`${px},-155 ${px + 100},0 ${px},0`} fill="#090d1f" />
                <polygon points={`${px},-155 ${px - 28},-110 ${px},-110`} fill="#f8fafc" />
                <polygon points={`${px},-155 ${px},-110 ${px + 30},-110`} fill="#94a3b8" />
              </g>
            ))}

            {/* ========================================================================= */}
            {/* 2. ÉTAGE INTERMÉDIAIRE : CHAÎNE MÉDIANE NOCTURNE                          */}
            {/* ========================================================================= */}

            {/* Pics intermédiaires centraux */}
            <polygon points="-80,0 -10,-100 -10,0" fill="#1e293b" />
            <polygon points="-10,-100 55,0 -10,0" fill="#090d1f" />
            <polygon points="-10,-100 -31,-70 -10,-70" fill="#e2e8f0" />
            <polygon points="-10,-100 -10,-70 10,-70" fill="#94a3b8" />

            <polygon points="45,0 115,-110 115,0" fill="#1e293b" />
            <polygon points="115,-110 185,0 115,0" fill="#090d1f" />
            <polygon points="115,-110 93,-75 115,-75" fill="#e2e8f0" />
            <polygon points="115,-110 115,-75 137,-75" fill="#94a3b8" />

            <polygon points="200,0 270,-105 270,0" fill="#1e293b" />
            <polygon points="270,-105 340,0 270,0" fill="#090d1f" />
            <polygon points="270,-105 248,-72 270,-72" fill="#e2e8f0" />
            <polygon points="270,-105 270,-72 292,-72" fill="#94a3b8" />

            <polygon points="325,0 390,-100 390,0" fill="#1e293b" />
            <polygon points="390,-100 460,0 390,0" fill="#090d1f" />
            <polygon points="390,-100 371,-70 390,-70" fill="#e2e8f0" />
            <polygon points="390,-100 390,-70 411,-70" fill="#94a3b8" />

            {/* Pics intermédiaires latéraux pour une chaîne continue */}
            {[-800, -500, -250, 600, 950, 1300].map((px, i) => (
              <g key={`djurdjura_mid_${i}`}>
                <polygon points={`${px - 65},0 ${px},-95 ${px},0`} fill="#1e293b" />
                <polygon points={`${px},-95 ${px + 65},0 ${px},0`} fill="#090d1f" />
                <polygon points={`${px},-95 ${px - 20},-68 ${px},-68`} fill="#e2e8f0" />
                <polygon points={`${px},-95 ${px},-68 ${px + 20},-68`} fill="#94a3b8" />
              </g>
            ))}

            {/* Voile sombre de brume nocturne d'altitude montant jusqu'en haut de l'écran pleine largeur (sans liseré plus clair) */}
            <path
              d="
                M -3000 -1200
                L 3400 -1200
                L 3400 -35
                Q 2000 -45 1000 -40 T 0 -35 T -1000 -40 T -3000 -35 Z
              "
              fill="#020617"
              opacity="0.32"
            />
            <path
              d="
                M -3000 -1200
                L 3400 -1200
                L 3400 -50
                Q 2000 -70 1000 -55 T 0 -45 T -1000 -55 T -3000 -50 Z
              "
              fill="#08142c"
              opacity="0.22"
            />

            {/* ========================================================================= */}
            {/* 3. CONTREFORTS INFÉRIEURS AU PREMIER PLAN                                  */}
            {/* ========================================================================= */}
            {/* Contreforts centraux */}
            <polygon points="-15,15 45,-50 45,15" fill="#0f172a" />
            <polygon points="45,-50 105,15 45,15" fill="#020617" />
            <polygon points="45,-50 28,-32 45,-32" fill="#e2e8f0" />
            <polygon points="45,-50 45,-32 62,-32" fill="#94a3b8" />

            <polygon points="120,15 180,-45 180,15" fill="#0f172a" />
            <polygon points="180,-45 240,15 180,15" fill="#020617" />
            <polygon points="180,-45 163,-28 180,-28" fill="#e2e8f0" />
            <polygon points="180,-45 180,-28 197,-28" fill="#94a3b8" />

            <polygon points="255,15 315,-50 315,15" fill="#0f172a" />
            <polygon points="315,-50 375,15 315,15" fill="#020617" />
            <polygon points="315,-50 298,-32 315,-32" fill="#e2e8f0" />
            <polygon points="315,-50 315,-32 332,-32" fill="#94a3b8" />

            <polygon points="365,15 415,-42 415,15" fill="#0f172a" />
            <polygon points="415,-42 465,15 415,15" fill="#020617" />
            <polygon points="415,-42 401,-26 415,-26" fill="#e2e8f0" />
            <polygon points="415,-42 415,-26 429,-26" fill="#94a3b8" />

            {/* Raccord nocturne pleine largeur avec la bordure du plateau */}
            <path
              d="
                M -3000 -12
                Q -2800 -25 -2600 -15 Q -2400 -8 -2200 -20 Q -2000 -25 -1800 -15
                Q -1600 -8 -1400 -20 Q -1200 -25 -1000 -15 Q -800 -8 -600 -20
                Q -400 -25 -200 -12 Q 0 -6 200 -18 Q 400 -25 600 -12
                Q 800 -8 1000 -18 Q 1200 -25 1400 -12 Q 1600 -8 1800 -18
                Q 2000 -25 2200 -12 Q 2400 -8 2600 -18 Q 2800 -25 3000 -12
                Q 3200 -8 3400 -12
                L 3400 0 L -3000 0 Z
              "
              fill="#475569"
            />
          </g>
        )}

        {/* --- LEVEL 3 : IƔIL (Crêtes & Forêt Mystique) --- */}
        {levelId === 3 && (
          <g id="horizon_level_3">
            {/* Ciel nocturne féerique violet-indigo pleine largeur */}
            <rect x="-3000" y="-2000" width="6400" height="2000" fill="url(#sky_igil)" />
            
            {/* Lune enchantée argentée et luminescente */}
            <g filter="url(#celestialGlow)">
              <circle cx="310" cy="-300" r="26" fill="#fdf4ff" />
              <circle cx="320" cy="-305" r="24" fill="#3b0764" />
            </g>

            {/* Orbes magiques flottantes et étincelles d'Iɣil réparties */}
            <circle cx="60" cy="-350" r="2" fill="#f0abfc" opacity="0.95" />
            <circle cx="130" cy="-380" r="1.5" fill="#ffffff" opacity="0.9" />
            <circle cx="210" cy="-330" r="2.2" fill="#67e8f9" opacity="0.95" />
            <circle cx="360" cy="-360" r="1.8" fill="#f0abfc" opacity="0.85" />
            <circle cx="260" cy="-390" r="1.4" fill="#ffffff" opacity="0.9" />
            <circle cx="-320" cy="-360" r="2" fill="#f0abfc" opacity="0.9" />
            <circle cx="-650" cy="-340" r="2.2" fill="#67e8f9" opacity="0.95" />
            <circle cx="750" cy="-350" r="2" fill="#f0abfc" opacity="0.9" />
            <circle cx="1150" cy="-370" r="1.8" fill="#67e8f9" opacity="0.95" />

            {/* Brume céleste luminescente pleine largeur */}
            <path
              d="
                M -3000 -260
                Q -2000 -310 -1000 -270 T 0 -280 T 1000 -270 T 2000 -280 T 3400 -260
                L 3400 -240 Q 2000 -230 1000 -250 T 0 -240 T -1000 -250 T -3000 -240 Z
              "
              fill="#d946ef"
              opacity="0.25"
            />

            {/* Monts magiques et silhouettes cristallines (-3000 à 3400) */}
            <polygon
              points="
                -3000,-90 -2650,-170 -2350,-110 -2050,-180 -1750,-120 -1450,-170 -1150,-100 -850,-180 -550,-110 -250,-160
                -50,-125 -10,-160 80,-125 180,-180 280,-120 370,-170 450,-120
                750,-170 1050,-110 1350,-180 1650,-120 1950,-170 2250,-100 2550,-180 2850,-110 3150,-170 3400,-90
                3400,0 -3000,0
              "
              fill="#2e1065"
              opacity="0.5"
            />
            {/* Obélisques & Cristaux / Arbres triangulaires féeriques fixés sous la crête de la colline #3b0764 */}
            <polygon points="78,-58 82,-92 86,-58" fill="#c026d3" />
            <polygon points="82,-92 86,-58 84,-58" fill="#f5d0fe" />
            <polygon points="340,-88 344,-128 348,-88" fill="#06b6d4" />
            <polygon points="344,-128 348,-88 346,-88" fill="#cffafe" />

            {/* Arbres triangulaires féeriques sur les crêtes latérales */}
            <polygon points="-420,-82 -416,-118 -412,-82" fill="#c026d3" />
            <polygon points="-416,-118 -412,-82 -414,-82" fill="#f5d0fe" />
            <polygon points="840,-54 844,-94 848,-54" fill="#06b6d4" />
            <polygon points="844,-94 848,-54 846,-54" fill="#cffafe" />

            <path
              d="
                M -3000 -60
                Q -2800 -105 -2600 -70 Q -2400 -45 -2200 -80 Q -2000 -110 -1800 -70
                Q -1600 -45 -1400 -85 Q -1200 -115 -1000 -70 Q -800 -45 -600 -80
                Q -400 -105 -200 -65 Q 0 -40 200 -85 Q 400 -110 600 -70
                Q 800 -45 1000 -80 Q 1200 -115 1400 -70 Q 1600 -45 1800 -80
                Q 2000 -110 2200 -70 Q 2400 -45 2600 -85 Q 2800 -115 3000 -70
                Q 3200 -45 3400 -60
                L 3400 0 L -3000 0 Z
              "
              fill="#3b0764"
            />

            {/* Raccord doux et harmonieux pleine largeur */}
            <path
              d="
                M -3000 -12
                Q -2800 -25 -2600 -15 Q -2400 -8 -2200 -20 Q -2000 -25 -1800 -15
                Q -1600 -8 -1400 -20 Q -1200 -25 -1000 -15 Q -800 -8 -600 -20
                Q -400 -25 -200 -12 Q 0 -6 200 -18 Q 400 -25 600 -12
                Q 800 -8 1000 -18 Q 1200 -25 1400 -12 Q 1600 -8 1800 -18
                Q 2000 -25 2200 -12 Q 2400 -8 2600 -18 Q 2800 -25 3000 -12
                Q 3200 -8 3400 -12
                L 3400 0 L -3000 0 Z
              "
              fill="#6b21a8"
            />
          </g>
        )}

        {/* --- LEVEL 4 : IJDUDEN / LǦEMƐA N TIZI (Le Sanctuaire Céleste) --- */}
        {levelId === 4 && (
          <g id="horizon_level_4">
            {/* 1. Couche Corrompue : Ciel d'orage toxique et aiguilles de basalte brisées */}
            <g
              id="ijduden_dark_horizon"
              style={{
                opacity: isRestored ? 0 : 1,
                transition: 'opacity 2500ms ease-in-out',
                pointerEvents: 'none'
              }}
            >
              {/* Ciel d'orage toxique et nébuleuse sombre pleine largeur */}
              <rect x="-3000" y="-2000" width="6400" height="2000" fill="url(#sky_ijduden_dark)" />
              
              {/* Lune céleste embrumée d'Ijduden abaissée pour un cadrage parfait sur mobile au-dessus de la crête */}
              <g id="ijduden_moon">
                {/* Halos lunaires discrets et tamisés */}
                <circle cx="200" cy="-75" r="48" fill="#10b981" opacity="0.12" filter="url(#celestialGlow)" />
                <circle cx="200" cy="-75" r="32" fill="#6ee7b7" opacity="0.18" />

                {/* Disque lunaire doux aux teintes argentées et lunaires feutrées */}
                <circle cx="200" cy="-75" r="21" fill="#94a3b8" opacity="0.9" />
                <circle cx="200" cy="-75" r="20" fill="#cbd5e1" opacity="0.85" />
                {/* Cratères et nuances lunaires subtiles */}
                <circle cx="194" cy="-79" r="3.2" fill="#64748b" opacity="0.4" />
                <circle cx="205" cy="-73" r="4.0" fill="#64748b" opacity="0.35" />
                <circle cx="199" cy="-69" r="2.0" fill="#64748b" opacity="0.3" />

                {/* Voiles de brume toxique et vaporeuse embrumant la lune */}
                <path
                  d="M 155 -83 Q 185 -91 215 -85 Q 235 -79 255 -83"
                  fill="none"
                  stroke="#6ee7b7"
                  strokeWidth="5"
                  strokeLinecap="round"
                  opacity="0.3"
                  filter="url(#celestialGlow)"
                />
                <path
                  d="M 165 -69 Q 190 -61 220 -70 Q 240 -74 250 -67"
                  fill="none"
                  stroke="#a7f3d0"
                  strokeWidth="6"
                  strokeLinecap="round"
                  opacity="0.25"
                  filter="url(#celestialGlow)"
                />
                <path
                  d="M 175 -77 Q 200 -83 228 -75"
                  fill="none"
                  stroke="#064e3b"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  opacity="0.35"
                />
                <ellipse cx="200" cy="-75" rx="36" ry="16" fill="#064e3b" opacity="0.22" filter="url(#celestialGlow)" />
              </g>

              {/* Étoiles scintillantes dans le ciel nocturne visibles sur mobile */}
              <circle cx="50" cy="-125" r="1.6" fill="#ffffff" opacity="0.85" />
              <circle cx="85" cy="-110" r="1.4" fill="#a7f3d0" opacity="0.8" />
              <circle cx="120" cy="-135" r="1.8" fill="#ffffff" opacity="0.9" />
              <circle cx="280" cy="-130" r="1.8" fill="#ffffff" opacity="0.9" />
              <circle cx="315" cy="-110" r="1.5" fill="#a7f3d0" opacity="0.8" />
              <circle cx="350" cy="-125" r="1.6" fill="#ffffff" opacity="0.85" />
              <circle cx="-180" cy="-240" r="1.8" fill="#ffffff" opacity="0.85" />
              <circle cx="580" cy="-250" r="1.8" fill="#ffffff" opacity="0.85" />

              {/* Sombres aiguilles de basalte brisées (col central abaissé à y=-40 pour dégager entièrement la lune) */}
              <polygon
                points="
                  -3000,-45 -2700,-110 -2450,-60 -2200,-120 -1950,-70 -1700,-115 -1450,-60 -1200,-110 -950,-65 -650,-115 -350,-65
                  -50,-80 -10,-105 40,-80 110,-95 145,-48 200,-40 255,-48 290,-95 350,-70 410,-105 450,-75
                  750,-115 1050,-60 1300,-115 1550,-70 1800,-120 2050,-65 2350,-110 2650,-60 2950,-115 3400,-45
                  3400,0 -3000,0
                "
                fill="#0f172a"
              />
              
              {/* Raccord doux et harmonieux pleine largeur */}
              <path
                d="
                  M -3000 -12
                  Q -2800 -25 -2600 -15 Q -2400 -8 -2200 -20 Q -2000 -25 -1800 -15
                  Q -1600 -8 -1400 -20 Q -1200 -25 -1000 -15 Q -800 -8 -600 -20
                  Q -400 -25 -200 -12 Q 0 -6 200 -18 Q 400 -25 600 -12
                  Q 800 -8 1000 -18 Q 1200 -25 1400 -12 Q 1600 -8 1800 -18
                  Q 2000 -25 2200 -12 Q 2400 -8 2600 -18 Q 2800 -25 3000 -12
                  Q 3200 -8 3400 -12
                  L 3400 0 L -3000 0 Z
                "
                fill="#27272a"
              />
            </g>

            {/* 2. Couche Jour Restauré : Ciel azur resplendissant, grand soleil radieux d'or, monts émeraude et oiseaux */}
            <g
              id="ijduden_day_horizon"
              style={{
                opacity: (isRestored && !isNight) ? 1 : 0,
                transition: 'opacity 2500ms ease-in-out',
                pointerEvents: 'none'
              }}
            >
              {/* Ciel azur resplendissant et purifié pleine largeur */}
              <rect x="-3000" y="-2000" width="6400" height="2000" fill="url(#sky_ijduden_restored)" />
              
              {/* Grand soleil céleste resplendissant d'or et d'émeraude abaissé pour mobile */}
              <g filter="url(#sunGlow)">
                <circle cx="200" cy="-75" r="85" fill="#fef08a" opacity="0.2" />
                <circle cx="200" cy="-75" r="60" fill="#34d399" opacity="0.4" />
                <circle cx="200" cy="-75" r="38" fill="#fef08a" opacity="0.95" />
                <circle cx="200" cy="-75" r="30" fill="#fffbeb" opacity="0.9" />
              </g>

              {/* Oiseaux de liberté volant dans le ciel purifié visibles sur mobile */}
              <path d="M 70 -105 Q 74 -110 78 -105 Q 82 -110 86 -105" fill="none" stroke="#065f46" strokeWidth="1.4" />
              <path d="M 90 -118 Q 93 -122 96 -118 Q 99 -122 102 -118" fill="none" stroke="#065f46" strokeWidth="1.1" />
              <path d="M 295 -100 Q 299 -105 303 -100 Q 307 -105 311 -100" fill="none" stroke="#065f46" strokeWidth="1.4" />
              <path d="M 315 -112 Q 318 -116 321 -112 Q 324 -116 327 -112" fill="none" stroke="#065f46" strokeWidth="1.1" />
              <path d="M -350 -220 Q -346 -225 -342 -220 Q -338 -225 -334 -220" fill="none" stroke="#065f46" strokeWidth="1.3" />
              <path d="M 780 -215 Q 784 -220 788 -215 Q 792 -220 796 -215" fill="none" stroke="#065f46" strokeWidth="1.3" />

              {/* Montagnes sacrées baignées de lumière émeraude et or avec col central ouvert */}
              <polygon
                points="
                  -3000,-50 -2650,-115 -2350,-65 -2050,-125 -1750,-70 -1450,-120 -1150,-65 -850,-120 -550,-65 -250,-115
                  -50,-80 -10,-105 70,-95 135,-48 200,-36 265,-48 330,-95 390,-115 450,-75
                  750,-120 1050,-65 1350,-125 1650,-70 1950,-120 2250,-65 2550,-120 2850,-70 3150,-115 3400,-50
                  3400,0 -3000,0
                "
                fill="#047857"
                opacity="0.4"
              />
              <path
                d="
                  M -3000 -60
                  Q -2800 -105 -2600 -70 Q -2400 -45 -2200 -80 Q -2000 -110 -1800 -70
                  Q -1600 -45 -1400 -85 Q -1200 -115 -1000 -70 Q -800 -45 -600 -80
                  Q -400 -105 -200 -65 Q -80 -75 0 -45 Q 80 -65 130 -42 Q 200 -34 270 -42 Q 320 -65 400 -45 Q 480 -75 600 -70
                  Q 800 -45 1000 -80 Q 1200 -115 1400 -70 Q 1600 -45 1800 -80
                  Q 2000 -110 2200 -70 Q 2400 -45 2600 -85 Q 2800 -115 3000 -70
                  Q 3200 -45 3400 -60
                  L 3400 0 L -3000 0 Z
                "
                fill="#15803d"
              />

              {/* Raccord doux et harmonieux pleine largeur */}
              <path
                d="
                  M -3000 -12
                  Q -2800 -25 -2600 -15 Q -2400 -8 -2200 -20 Q -2000 -25 -1800 -15
                  Q -1600 -8 -1400 -20 Q -1200 -25 -1000 -15 Q -800 -8 -600 -20
                  Q -400 -25 -200 -12 Q 0 -6 200 -18 Q 400 -25 600 -12
                  Q 800 -8 1000 -18 Q 1200 -25 1400 -12 Q 1600 -8 1800 -18
                  Q 2000 -25 2200 -12 Q 2400 -8 2600 -18 Q 2800 -25 3000 -12
                  Q 3200 -8 3400 -12
                  L 3400 0 L -3000 0 Z
                "
                fill="#22c55e"
              />
            </g>

            {/* 3. Couche Nuit Épilogue : Activée dès que la sorcière dit 'Attendez... Regardez le ciel au-dessus de nous !' */}
            <g
              id="ijduden_night_horizon"
              style={{
                opacity: isNight ? 1 : 0,
                transition: 'opacity 2500ms ease-in-out',
                pointerEvents: 'none'
              }}
            >
              {/* Ciel nocturne profond pleine largeur */}
              <rect x="-3000" y="-2000" width="6400" height="2000" fill="url(#sky_ijduden_night)" />
              
              {/* Lune céleste embrumée d'Ijduden (Ayyur) abaissée au-dessus de la crête */}
              <g id="ijduden_moon_night">
                <circle cx="200" cy="-75" r="48" fill="#10b981" opacity="0.12" filter="url(#celestialGlow)" />
                <circle cx="200" cy="-75" r="32" fill="#6ee7b7" opacity="0.18" />

                <circle cx="200" cy="-75" r="21" fill="#94a3b8" opacity="0.9" />
                <circle cx="200" cy="-75" r="20" fill="#cbd5e1" opacity="0.85" />
                <circle cx="194" cy="-79" r="3.2" fill="#64748b" opacity="0.4" />
                <circle cx="205" cy="-73" r="4.0" fill="#64748b" opacity="0.35" />
                <circle cx="199" cy="-69" r="2.0" fill="#64748b" opacity="0.3" />

                <path
                  d="M 155 -83 Q 185 -91 215 -85 Q 235 -79 255 -83"
                  fill="none"
                  stroke="#6ee7b7"
                  strokeWidth="5"
                  strokeLinecap="round"
                  opacity="0.3"
                  filter="url(#celestialGlow)"
                />
                <path
                  d="M 165 -69 Q 190 -61 220 -70 Q 240 -74 250 -67"
                  fill="none"
                  stroke="#a7f3d0"
                  strokeWidth="6"
                  strokeLinecap="round"
                  opacity="0.25"
                  filter="url(#celestialGlow)"
                />
                <path
                  d="M 175 -77 Q 200 -83 228 -75"
                  fill="none"
                  stroke="#064e3b"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  opacity="0.35"
                />
                <ellipse cx="200" cy="-75" rx="36" ry="16" fill="#064e3b" opacity="0.22" filter="url(#celestialGlow)" />
              </g>

              {/* Étoiles scintillantes dans le ciel nocturne */}
              <circle cx="50" cy="-125" r="1.6" fill="#ffffff" opacity="0.85" />
              <circle cx="85" cy="-110" r="1.4" fill="#a7f3d0" opacity="0.8" />
              <circle cx="120" cy="-135" r="1.8" fill="#ffffff" opacity="0.9" />
              <circle cx="280" cy="-130" r="1.8" fill="#ffffff" opacity="0.9" />
              <circle cx="315" cy="-110" r="1.5" fill="#a7f3d0" opacity="0.8" />
              <circle cx="350" cy="-125" r="1.6" fill="#ffffff" opacity="0.85" />
              <circle cx="-180" cy="-240" r="1.8" fill="#ffffff" opacity="0.85" />
              <circle cx="580" cy="-250" r="1.8" fill="#ffffff" opacity="0.85" />

              {/* Sombres aiguilles de basalte brisées */}
              <polygon
                points="
                  -3000,-45 -2700,-110 -2450,-60 -2200,-120 -1950,-70 -1700,-115 -1450,-60 -1200,-110 -950,-65 -650,-115 -350,-65
                  -50,-80 -10,-105 40,-80 110,-95 145,-48 200,-40 255,-48 290,-95 350,-70 410,-105 450,-75
                  750,-115 1050,-60 1300,-115 1550,-70 1800,-120 2050,-65 2350,-110 2650,-60 2950,-115 3400,-45
                  3400,0 -3000,0
                "
                fill="#0f172a"
              />
              
              {/* Raccord doux et harmonieux pleine largeur */}
              <path
                d="
                  M -3000 -12
                  Q -2800 -25 -2600 -15 Q -2400 -8 -2200 -20 Q -2000 -25 -1800 -15
                  Q -1600 -8 -1400 -20 Q -1200 -25 -1000 -15 Q -800 -8 -600 -20
                  Q -400 -25 -200 -12 Q 0 -6 200 -18 Q 400 -25 600 -12
                  Q 800 -8 1000 -18 Q 1200 -25 1400 -12 Q 1600 -8 1800 -18
                  Q 2000 -25 2200 -12 Q 2400 -8 2600 -18 Q 2800 -25 3000 -12
                  Q 3200 -8 3400 -12
                  L 3400 0 L -3000 0 Z
                "
                fill="#27272a"
              />
            </g>
          </g>
        )}

        {/* ========================================================================= */}
        {/* 1.5 SÉPARATION HORIZON / CARTE : MURETS, BARRIÈRES & PARAPETS THÉMATISÉS   */}
        {/* (Bordure supérieure y = -16 à y = 14 séparant l'horizon du plateau de jeu)  */}
        {/* ========================================================================= */}

        {/* --- MURET LEVEL 0 : TADDART (Afrag n yeẓra - Muret traditionnel en pierres sèches & lisses de bois) --- */}
        {levelId === 0 && (
          <g id="horizon_barrier_level_0">
            {/* Ombre portée du muret sur le sol */}
            <rect x="-3000" y="-3" width="6400" height="3" fill="#000000" opacity="0.2" />
            
            {/* Corps du muret en pierres empilées */}
            <rect x="-3000" y="-14" width="6400" height="14" fill="#57534e" />
            <path d="M -3000 -14 L 3400 -14 L 3400 -18 L -3000 -18 Z" fill="#78716c" />
            <rect x="-3000" y="-18" width="6400" height="3" fill="#a8a29e" />

            {/* Traverses horizontales en bois d'olivier rustique */}
            <rect x="-3000" y="-22" width="6400" height="3.5" rx="1" fill="#713f12" />
            <rect x="-3000" y="-22" width="6400" height="1.2" fill="#854d0e" />

            {/* Piliers verticaux en bois et renforts de pierre répétés sur toute la largeur */}
            {posts.map((px) => (
              <g key={`taddart_post_${px}`}>
                <rect x={px} y="-26" width="7" height="26" rx="1.5" fill="#5c2e0b" />
                <rect x={px + 1} y="-26" width="2" height="26" fill="#a16207" opacity="0.6" />
                <polygon points={`${px - 1},-26 ${px + 3.5},-30 ${px + 8},-26`} fill="#78350f" />
                <rect x={px - 2} y="-16" width="11" height="16" rx="1" fill="#78716c" />
                <rect x={px - 3} y="-18" width="13" height="3" rx="0.5" fill="#a8a29e" />
              </g>
            ))}

            {/* Touffes de lierre et fleurs sauvages */}
            <circle cx="45" cy="-5" r="4" fill="#15803d" />
            <circle cx="160" cy="-4" r="3.5" fill="#166534" />
            <circle cx="280" cy="-5" r="4.5" fill="#15803d" />
            <circle cx="395" cy="-4" r="4" fill="#166534" />
            <circle cx="46" cy="-6" r="1.5" fill="#fef08a" />
            <circle cx="282" cy="-6" r="1.5" fill="#fef08a" />
          </g>
        )}

        {/* --- MURET LEVEL 1 : AZEMMUR (Tabiyya - Clôture d'oliveraie & muret en grès ocre et terre cuite) --- */}
        {levelId === 1 && (
          <g id="horizon_barrier_level_1">
            {/* Ombre portée */}
            <rect x="-3000" y="-3" width="6400" height="3" fill="#000000" opacity="0.25" />

            {/* Muret en grès ocre chaud */}
            <rect x="-3000" y="-14" width="6400" height="14" fill="#78350f" />
            <path d="M -3000 -14 L 3400 -14 L 3400 -18 L -3000 -18 Z" fill="#92400e" />
            <rect x="-3000" y="-18" width="6400" height="3" fill="#b45309" />

            {/* Lisse supérieure en bois d'olivier tressé */}
            <rect x="-3000" y="-27" width="6400" height="4" rx="1" fill="#713f12" />
            <rect x="-3000" y="-17" width="6400" height="2.5" rx="0.5" fill="#854d0e" />

            {/* Piliers d'angle et de soutènement répétés sur toute la largeur */}
            {posts.map((px) => (
              <g key={`azemmur_post_${px}`}>
                <rect x={px} y="-23" width="8" height="23" rx="1" fill="#451a03" />
                <rect x={px + 1} y="-23" width="2" height="23" fill="#b45309" opacity="0.7" />
                <circle cx={px + 4} cy="-29" r="3" fill="#92400e" />
                <rect x={px - 2} y="-16" width="12" height="16" rx="1" fill="#92400e" />
                <rect x={px - 3} y="-18" width="14" height="3" rx="0.5" fill="#b45309" />
                {/* Jarres traditionnelles en terre cuite sur certains piliers */}
                {(Math.abs(px) % 110 === 0 || px === 85 || px === 315) && (
                  <g transform={`translate(${px}, -29) scale(0.9)`}>
                    <ellipse cx="4" cy="5" rx="5" ry="6" fill="#c2410c" />
                    <ellipse cx="4" cy="0" rx="3" ry="1.5" fill="#ea580c" />
                  </g>
                )}
              </g>
            ))}

            {/* Feuillages d'olivier qui retombent */}
            <ellipse cx="145" cy="-5" rx="8" ry="4" fill="#3f6212" />
            <ellipse cx="265" cy="-5" rx="7" ry="3.5" fill="#365314" />
            <circle cx="147" cy="-4" r="1.5" fill="#1c1917" />
          </g>
        )}

        {/* --- MURET LEVEL 2 : DJURDJURA (Garde-corps alpin en calcaire & pin avec neige immaculée) --- */}
        {levelId === 2 && (
          <g id="horizon_barrier_level_2">
            {/* Ombre portée sur la roche */}
            <rect x="-3000" y="-3" width="6400" height="3" fill="#000000" opacity="0.3" />

            {/* Muret de soutènement en blocs de calcaire gris alpin */}
            <rect x="-3000" y="-14" width="6400" height="14" fill="#1e293b" />
            <path d="M -3000 -14 L 3400 -14 L 3400 -18 L -3000 -18 Z" fill="#334155" />
            <rect x="-3000" y="-18" width="6400" height="3" fill="#475569" />

            {/* Couche de neige continue sur le chaperon du muret */}
            <rect x="-3000" y="-18" width="6400" height="3.5" rx="1" fill="#f8fafc" />

            {/* Lisse supérieure en bois de pin sombre */}
            <rect x="-3000" y="-24" width="6400" height="3.5" rx="1" fill="#0f172a" />
            <rect x="-3000" y="-24" width="6400" height="1.2" fill="#64748b" />
            
            {/* Neige sur la lisse supérieure */}
            <rect x="-3000" y="-25" width="6400" height="1.8" rx="0.5" fill="#ffffff" />

            {/* Piliers massifs en pierre et pin répétés sur toute la largeur */}
            {posts.map((px) => (
              <g key={`djurdjura_post_${px}`}>
                <rect x={px} y="-28" width="8" height="28" rx="1" fill="#0f172a" />
                <rect x={px + 1} y="-28" width="2" height="28" fill="#475569" opacity="0.8" />
                {/* Calotte de neige sur le poteau */}
                <ellipse cx={px + 4} cy="-28" rx="5.5" ry="2.2" fill="#ffffff" />
                <rect x={px - 2} y="-16" width="12" height="16" rx="1" fill="#334155" />
                <rect x={px - 3} y="-18" width="14" height="3" rx="0.5" fill="#475569" />
                <path d={`M ${px - 3} -18 Q ${px + 4} -21 ${px + 11} -18 L ${px + 11} -16 L ${px - 3} -16 Z`} fill="#ffffff" />
              </g>
            ))}
          </g>
        )}

        {/* --- MURET LEVEL 3 : IƔIL (Balustrade féerique en basalte d'améthyste & orbes bioluminescentes) --- */}
        {levelId === 3 && (
          <g id="horizon_barrier_level_3">
            {/* Ombre violette */}
            <rect x="-3000" y="-3" width="6400" height="3" fill="#0c0728" opacity="0.35" />

            {/* Parapet en basalte mystique */}
            <rect x="-3000" y="-14" width="6400" height="14" fill="#1e1b4b" />
            <path d="M -3000 -14 L 3400 -14 L 3400 -18 L -3000 -18 Z" fill="#2e1065" />
            <rect x="-3000" y="-18" width="6400" height="3" fill="#3b0764" />

            {/* Liseré luminescent d'améthyste */}
            <rect x="-3000" y="-15" width="6400" height="1.5" fill="#c026d3" opacity="0.8" />

            {/* Balustrade sculptée en arcs ogivaux */}
            <rect x="-3000" y="-27" width="6400" height="4" rx="1" fill="#2e1065" />
            <rect x="-3000" y="-27" width="6400" height="1" fill="#7e22ce" />

            {/* Piliers et orbes enchantées répétés sur toute la largeur */}
            {posts.map((px) => (
              <g key={`igil_post_${px}`}>
                <rect x={px} y="-23" width="8" height="23" rx="1.5" fill="#1e1b4b" />
                <rect x={px + 1} y="-23" width="2" height="23" fill="#6b21a8" />
                
                {/* Orbe cristalline féerique scintillante */}
                <circle cx={px + 4} cy="-31.5" r="4.5" fill="#f0abfc" opacity="0.95" filter="url(#celestialGlow)" />
                <circle cx={px + 4} cy="-31.5" r="2.2" fill="#67e8f9" />

                <rect x={px - 2} y="-16" width="12" height="16" rx="1" fill="#2e1065" />
                <rect x={px - 3} y="-18" width="14" height="3" rx="0.5" fill="#3b0764" />
              </g>
            ))}

            {/* Vrilles luminescentes grimpantes */}
            <ellipse cx="60" cy="-5" rx="5" ry="3" fill="#581c87" />
            <circle cx="61" cy="-6" r="1.5" fill="#f0abfc" opacity="0.9" />
            <ellipse cx="280" cy="-5" rx="6" ry="3.5" fill="#581c87" />
            <circle cx="281" cy="-6" r="1.5" fill="#67e8f9" opacity="0.9" />
          </g>
        )}

        {/* --- MURET LEVEL 4 : IJDUDEN (Sanctuaire Antique Corrompu / Restauré) --- */}
        {levelId === 4 && (
          <g id="horizon_barrier_level_4">
            {/* Mode Corrompu : Ruines brisées de pierre noire & fer forgé antique tordu */}
            <g
              id="corrupted_barrier"
              style={{
                opacity: isRestored ? 0 : 1,
                transition: 'opacity 2500ms ease-in-out',
                pointerEvents: 'none'
              }}
            >
              <rect x="-3000" y="-3" width="6400" height="3" fill="#000000" opacity="0.35" />

              {/* Muret effondré et brisé en dents de scie */}
              <rect x="-3000" y="-14" width="6400" height="14" fill="#18181b" />
              <rect x="-3000" y="-17" width="6400" height="3" fill="#27272a" opacity="0.8" />

              {/* Piques et barreaux de fer rouillé tordus */}
              <polygon points="25,-16 27,-26 29,-16" fill="#0f172a" />
              <polygon points="65,-15 67,-24 69,-15" fill="#334155" />
              <polygon points="160,-15 163,-28 165,-15" fill="#0f172a" />
              <polygon points="280,-15 282,-25 285,-15" fill="#334155" />
              <polygon points="345,-15 348,-27 350,-15" fill="#0f172a" />

              {/* Piliers brisés répétés sur toute la largeur */}
              {posts.map((px) => (
                <g key={`dark_post_${px}`}>
                  <rect x={px} y="-22" width="10" height="22" rx="1" fill="#18181b" />
                  <polygon points={`${px},-22 ${px + 5},-26 ${px + 10},-22`} fill="#27272a" />
                  {/* Fissure toxique verte */}
                  <line x1={px + 4} y1="-18" x2={px + 6} y2="-6" stroke="#34d399" strokeWidth="1.2" opacity="0.75" />
                </g>
              ))}
            </g>

            {/* Mode Jour Restauré (Lǧemɛa n Tizi) : Muraille sacrée continue d'émeraude et d'or fin avec Yaz ⵣ */}
            <g
              id="restored_barrier_day"
              style={{
                opacity: (isRestored && !isNight) ? 1 : 0,
                transition: 'opacity 2500ms ease-in-out',
                pointerEvents: 'none'
              }}
            >
              {/* Ombre dorée et verte */}
              <rect x="-3000" y="-3" width="6400" height="3" fill="#022c22" opacity="0.25" />

              {/* Muraille pleine continue en marbre vert impérial (aucun trou ni vide) */}
              <rect x="-3000" y="-24" width="6400" height="24" fill="#064e3b" />
              <rect x="-3000" y="-23" width="6400" height="9.5" fill="#065f46" />
              
              {/* Frise intermédiaire d'émeraude */}
              <rect x="-3000" y="-15" width="6400" height="2" fill="#059669" />

              {/* Liseré d'or pur ciselé */}
              <rect x="-3000" y="-14" width="6400" height="1.8" fill="#fbbf24" />

              {/* Lisse supérieure et chaperon en or et marbre d'émeraude */}
              <rect x="-3000" y="-25.5" width="6400" height="3.5" rx="1" fill="#047857" />
              <rect x="-3000" y="-25.5" width="6400" height="1.2" fill="#fbbf24" />
              <rect x="-3000" y="-23" width="6400" height="1.2" fill="#fef08a" />

              {/* Colonnettes ouvragées avec chapiteaux dorés et symboles Yaz ⵣ */}
              {posts.map((px) => (
                <g key={`restored_post_${px}`}>
                  <rect x={px} y="-27" width="8" height="27" rx="1" fill="#065f46" />
                  <rect x={px + 1} y="-27" width="2" height="27" fill="#34d399" />
                  
                  {/* Chapiteau doré */}
                  <polygon points={`${px - 1},-27 ${px + 4},-32 ${px + 9},-27`} fill="#f59e0b" />
                  <circle cx={px + 4} cy="-32" r="2" fill="#fef08a" />

                  <rect x={px - 2} y="-16" width="12" height="16" rx="1" fill="#059669" />
                  <rect x={px - 3} y="-18" width="14" height="3" rx="0.5" fill="#10b981" />

                  {/* Symbole Yaz ⵣ sculpté en or */}
                  <text x={px + 4} y="-19" fill="#fef08a" fontSize="6.5" fontWeight="bold" textAnchor="middle" fontFamily="sans-serif">
                    ⵣ
                  </text>
                </g>
              ))}
            </g>

            {/* Mode Nuit Épilogue (Lǧemɛa n Tizi) : Muraille sacrée nocturne continue sous le clair de lune avec Yaz ⵣ éclatant */}
            <g
              id="restored_barrier_night"
              style={{
                opacity: isNight ? 1 : 0,
                transition: 'opacity 2500ms ease-in-out',
                pointerEvents: 'none'
              }}
            >
              {/* Ombre nocturne profonde */}
              <rect x="-3000" y="-3" width="6400" height="3" fill="#01140e" opacity="0.5" />

              {/* Muraille pleine continue émeraude sombre nocturne (aucun trou ni vide) */}
              <rect x="-3000" y="-24" width="6400" height="24" fill="#022c22" />
              <rect x="-3000" y="-23" width="6400" height="9.5" fill="#064e3b" />
              
              {/* Frise intermédiaire nocturne */}
              <rect x="-3000" y="-15" width="6400" height="2" fill="#047857" />

              {/* Liseré d'or antique au clair de lune */}
              <rect x="-3000" y="-14" width="6400" height="1.8" fill="#d97706" />

              {/* Lisse supérieure en or ciselé sous la lune */}
              <rect x="-3000" y="-25.5" width="6400" height="3.5" rx="1" fill="#064e3b" />
              <rect x="-3000" y="-25.5" width="6400" height="1.2" fill="#d97706" />
              <rect x="-3000" y="-23" width="6400" height="1.2" fill="#fde047" opacity="0.8" />

              {/* Colonnettes ouvragées nocturnes avec symboles Yaz ⵣ dorés luminescents */}
              {posts.map((px) => (
                <g key={`restored_night_post_${px}`}>
                  <rect x={px} y="-27" width="8" height="27" rx="1" fill="#022c22" />
                  <rect x={px + 1} y="-27" width="2" height="27" fill="#059669" />
                  
                  {/* Chapiteau doré nocturne */}
                  <polygon points={`${px - 1},-27 ${px + 4},-32 ${px + 9},-27`} fill="#d97706" />
                  <circle cx={px + 4} cy="-32" r="2" fill="#fef08a" />

                  <rect x={px - 2} y="-16" width="12" height="16" rx="1" fill="#064e3b" />
                  <rect x={px - 3} y="-18" width="14" height="3" rx="0.5" fill="#34d399" opacity="0.6" />

                  {/* Symbole Yaz ⵣ sculpté en or luminescent */}
                  <text x={px + 4} y="-19" fill="#fef08a" fontSize="6.5" fontWeight="bold" textAnchor="middle" fontFamily="sans-serif">
                    ⵣ
                  </text>
                </g>
              ))}
            </g>
          </g>
        )}
      </svg>
    </div>
  );
});
